import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Autocomplete,
  IconButton,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBackIos,
  ArrowForwardIos,
  TrendingDown as TrendingDownIcon,
  LocalShipping as LocalShippingIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { functions, db } from '../index';
import { makes, getModels, getBodyTypes } from '../data/vehicle-data';
import citiesRaw from '../data/us-cities-zip.json';
import VehicleCard from '../components/VehicleCard';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const exteriorColors = [
  { label: 'Black', hex: '#212121' },
  { label: 'White', hex: '#f5f5f5', border: '#bdbdbd' },
  { label: 'Silver', hex: '#b0b0b0' },
  { label: 'Gray', hex: '#757575' },
  { label: 'Red', hex: '#d32f2f' },
  { label: 'Blue', hex: '#1565c0' },
  { label: 'Brown', hex: '#6d4c41' },
  { label: 'Green', hex: '#2e7d32' },
  { label: 'Orange', hex: '#e65100' },
  { label: 'Gold', hex: '#c6a700' },
  { label: 'Yellow', hex: '#f9a825' },
  { label: 'Purple', hex: '#7b1fa2' },
  { label: 'Beige', hex: '#d7ccc8', border: '#a1887f' },
];

const fuelTypes = ['Gas', 'Diesel', 'Electric', 'Hybrid', 'Plug-In Hybrid'];
const bodyTypes = ['Sedan', 'SUV', 'Truck', 'Coupe', 'Convertible', 'Van', 'Wagon', 'Hatchback', 'Minivan'];
const drivetrainOptions = ['FWD', 'RWD', 'AWD', '4WD'];
const radiusOptions = [10, 25, 50, 75, 100, 150, 200, 500, 'nationwide'];

const defaultFilters = {
  make: '',
  model: '',
  yearMin: 2015,
  yearMax: new Date().getFullYear(),
  priceMax: 50000,
  maxMileage: 100000,
  exteriorColor: [],
  zipCode: '',
  radius: 100,
  engineType: [],
  bodyType: '',
  drivetrain: [],
  cleanTitle: true,
};

function loadSessionState() {
  try {
    const saved = sessionStorage.getItem('vehicleSearch');
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return null;
}

const MAX_RECENT = 8;

function loadRecentSearches() {
  try {
    const raw = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const seen = new Set();
    const deduped = raw.filter((r) => {
      const k = recentSearchKey(r.filters);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (deduped.length < raw.length) {
      localStorage.setItem('recentSearches', JSON.stringify(deduped));
    }
    return deduped;
  } catch { return []; }
}

function recentSearchKey(f) {
  return JSON.stringify({ make: f.make, model: f.model, yearMin: f.yearMin, yearMax: f.yearMax });
}

function saveRecentSearch(filters) {
  const recent = loadRecentSearches();
  const key = recentSearchKey(filters);
  const filtered = recent.filter((r) => recentSearchKey(r.filters) !== key);
  filtered.unshift({ filters, timestamp: Date.now() });
  localStorage.setItem('recentSearches', JSON.stringify(filtered.slice(0, MAX_RECENT)));
}

function formatSearchLabel(f) {
  const parts = [f.make];
  if (f.model) parts.push(f.model);
  if (f.yearMin || f.yearMax) parts.push(`${f.yearMin || '?'}-${f.yearMax || '?'}`);
  if (f.bodyType) parts.push(f.bodyType);
  const dt = [].concat(f.drivetrain || []).filter(Boolean);
  if (dt.length) parts.push(dt.join('/'));
  const ec = [].concat(f.exteriorColor || []).filter(Boolean);
  if (ec.length) parts.push(ec.join('/'));
  if (f.radius === 'nationwide') parts.push('Nationwide');
  else if (f.zipCode) parts.push(f.zipCode);
  return parts.join(' ');
}

/* ─── Horizontally-scrollable card row with arrows ─── */
function ScrollRow({ vehicles, title }) {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.75;
    scrollRef.current.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (!vehicles || vehicles.length === 0) return null;

  return (
    <Box sx={{ mb: 4, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h5" fontWeight="bold">{title}</Typography>
        <Box>
          <IconButton size="small" onClick={() => scroll(-1)}><ArrowBackIos fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => scroll(1)}><ArrowForwardIos fontSize="small" /></IconButton>
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          maxWidth: '100%',
          scrollSnapType: 'x mandatory',
          pb: 1,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
        }}
      >
        {vehicles.map((v) => (
          <Box key={v.id} sx={{ minWidth: 260, maxWidth: 300, flex: '0 0 auto', scrollSnapAlign: 'start' }}>
            <VehicleCard vehicle={v} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ─── Main component ─── */
function VehicleSearch() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const cached = loadSessionState();
  const [filters, setFilters] = useState(cached?.filters || defaultFilters);
  const [results, setResults] = useState(cached?.results || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState(loadRecentSearches);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  const [cheaperMarkets, setCheaperMarkets] = useState(cached?.cheaperMarkets || []);
  const [cheaperMarketsSearched, setCheaperMarketsSearched] = useState(cached?.cheaperMarketsSearched || false);
  const [savingSearch, setSavingSearch] = useState(false);
  const { isAnonymous } = useAuth();

  const handleSaveSearch = async () => {
    try {
      setSavingSearch(true);
      const createSavedSearch = httpsCallable(functions, 'api-createSavedSearch');
      const name = [filters.make, filters.model, filters.zipCode].filter(Boolean).join(' — ') || 'My Search';
      await createSavedSearch({
        name,
        searchParams: filters,
        notifyNew: true,
        notifyPriceDrop: true,
      });
      alert('Search saved! You can view it in Saved Searches.');
    } catch (err) {
      console.error('Save search failed:', err);
      if (err.code === 'functions/already-exists') {
        alert('You\'ve already saved this search.');
      } else {
        alert(err.message || 'Failed to save search. Please try again.');
      }
    } finally {
      setSavingSearch(false);
    }
  };
  const [locationInput, setLocationInput] = useState(cached?.filters?.zipCode || '');
  const [nominatimResults, setNominatimResults] = useState([]);
  const nominatimTimer = useRef(null);
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);

  // Get user's location on mount for proximity sorting
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); },
        () => { /* permission denied — no sorting, that's fine */ },
        { timeout: 5000, maximumAge: 600000 }
      );
    }
  }, []);

  // Parse cities data: [[city, state, zip, lat, lng], ...] → [{city, state, zip, lat, lng}, ...]
  const locationSuggestions = useMemo(() =>
    citiesRaw.map(([city, state, zip, lat, lng]) => ({ city, state, zip, lat, lng })),
  []);

  // Nominatim search — debounced, always runs alongside local matches
  const fetchNominatim = (query) => {
    if (nominatimTimer.current) clearTimeout(nominatimTimer.current);
    setNominatimResults([]);
    if (!query || query.length < 3 || /^\d/.test(query)) return;

    nominatimTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=us&format=json&addressdetails=1&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        // For results without a postcode, reverse-geocode to get one
        const results = [];
        for (const r of data) {
          if (!r.address) continue;
          const city = r.address.city || r.address.town || r.address.village || r.address.hamlet || r.address.suburb || r.name;
          const state = r.address.state || '';
          let zip = r.address.postcode || '';
          if (!zip && r.lat && r.lon) {
            try {
              const rev = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${r.lat}&lon=${r.lon}&format=json&addressdetails=1`,
                { headers: { 'Accept-Language': 'en' } }
              );
              const revData = await rev.json();
              zip = revData?.address?.postcode || '';
            } catch (e) { /* ignore */ }
          }
          if (city && zip) {
            results.push({ city, state, zip, source: 'nominatim' });
          }
        }
        setNominatimResults(results);
      } catch (err) {
        console.warn('Nominatim lookup failed:', err);
      }
    }, 400);
  };

  // Featured / nearby vehicles
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  const hasResults = results.length > 0;

  const availableModels = filters.make ? getModels(filters.make) : [];
  const availableBodyTypes = filters.model ? (getBodyTypes(filters.model) || bodyTypes) : bodyTypes;

  const sortedResults = React.useMemo(() => {
    if (!hasResults) return [];
    const sorted = [...results];
    switch (sortBy) {
      case 'price_asc':  return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price_desc': return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'miles_asc':  return sorted.sort((a, b) => (a.mileage || 0) - (b.mileage || 0));
      case 'miles_desc': return sorted.sort((a, b) => (b.mileage || 0) - (a.mileage || 0));
      case 'year_desc':  return sorted.sort((a, b) => (b.year || 0) - (a.year || 0));
      case 'year_asc':   return sorted.sort((a, b) => (a.year || 0) - (b.year || 0));
      case 'savings':    return sorted.sort((a, b) => (b.market_savings || 0) - (a.market_savings || 0));
      case 'newest':     return sorted.sort((a, b) => (a.dom || 999) - (b.dom || 999));
      default:           return sorted;
    }
  }, [results, sortBy, hasResults]);

  // Detect user location via IP and load featured vehicles
  useEffect(() => {
    let cancelled = false;

    const loadFeatured = async () => {
      // Try IP geolocation for location label
      try {
        const geo = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
        if (geo.ok) {
          const data = await geo.json();
          if (!cancelled && data.city) {
            setUserLocation({ city: data.city, region: data.region, zip: data.postal });
          }
        }
      } catch { /* ignore — we'll still show featured vehicles */ }

      // Load recent vehicles from Firestore
      try {
        const q = query(
          collection(db, 'vehicles'),
          orderBy('last_updated', 'desc'),
          limit(20),
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setFeatured(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error('Error loading featured vehicles:', err);
      } finally {
        if (!cancelled) setFeaturedLoading(false);
      }
    };

    if (!hasResults) loadFeatured();
    else setFeaturedLoading(false);

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-search when opened via URL params (e.g., from "Search this market" link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('make')) {
      const urlFilters = {
        ...defaultFilters,
        make: params.get('make') || '',
        model: params.get('model') || '',
        yearMin: parseInt(params.get('yearMin')) || defaultFilters.yearMin,
        yearMax: parseInt(params.get('yearMax')) || defaultFilters.yearMax,
        priceMax: parseInt(params.get('priceMax')) || defaultFilters.priceMax,
        maxMileage: parseInt(params.get('maxMileage')) || defaultFilters.maxMileage,
        zipCode: params.get('zipCode') || '',
        radius: params.get('radius') === 'nationwide' ? 'nationwide' : (parseInt(params.get('radius')) || defaultFilters.radius),
        exteriorColor: params.get('exteriorColor') ? params.get('exteriorColor').split(',') : [],
        engineType: params.get('engineType') ? params.get('engineType').split(',') : [],
        bodyType: params.get('bodyType') || '',
        drivetrain: params.get('drivetrain') ? params.get('drivetrain').split(',') : [],
        cleanTitle: params.has('cleanTitle') ? params.get('cleanTitle') !== 'false' : defaultFilters.cleanTitle,
      };
      setFilters(urlFilters);
      window.history.replaceState({}, '', window.location.pathname);
      handleSearch(urlFilters);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const maxAllowedYear = new Date().getFullYear() + 1;

  const handleFilterChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'yearMax' && Number(value) > maxAllowedYear) value = maxAllowedYear;
    if (field === 'yearMin' && Number(value) > maxAllowedYear) value = maxAllowedYear;
    setFilters((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'make') {
        updated.model = '';
        updated.bodyType = '';
      }
      if (field === 'model') {
        // Clear body type if it's no longer valid for the new model
        const allowed = getBodyTypes(value);
        if (allowed && updated.bodyType && !allowed.includes(updated.bodyType)) {
          updated.bodyType = '';
        }
      }
      return updated;
    });
  };

  const handleSearch = async (overrideFilters) => {
    const searchFilters = overrideFilters || filters;
    if (!searchFilters.make) {
      setError('Please select a make');
      return;
    }
    if (searchFilters.radius === 'nationwide') {
      if (!searchFilters.model) {
        setError('Nationwide search requires a model to be selected');
        return;
      }
      const yearRange = (searchFilters.yearMax || new Date().getFullYear()) - (searchFilters.yearMin || 2015);
      if (yearRange > 1) {
        setError('Nationwide search requires a year range of 1 year or less (e.g. 2024-2025)');
        return;
      }
    }
    try {
      setLoading(true);
      setError(null);
      const searchVehicles = httpsCallable(functions, 'api-searchVehicles');
      const result = await searchVehicles({ ...searchFilters, liveApi: true });

      if (result.data.success) {
        const mapped = result.data.vehicles.map((v, i) => ({ id: v.id || i, ...v }));
        const markets = result.data.cheaper_markets || [];
        const marketsSearched = result.data.cheaper_markets_searched || false;
        setResults(mapped);
        setCheaperMarkets(markets);
        setCheaperMarketsSearched(marketsSearched);
        sessionStorage.setItem('vehicleSearch', JSON.stringify({ filters: searchFilters, results: mapped, cheaperMarkets: markets, cheaperMarketsSearched: marketsSearched }));
        saveRecentSearch(searchFilters);
        setRecentSearches(loadRecentSearches());
        if (result.data.vehicles.length === 0) {
          setError('No vehicles found matching your criteria');
        }
      } else {
        setError('Search returned no results');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching');
    } finally {
      setLoading(false);
    }
  };

  const handleClearResults = () => {
    setResults([]);
    setCheaperMarkets([]);
    setCheaperMarketsSearched(false);
    setFilters(defaultFilters);
    sessionStorage.removeItem('vehicleSearch');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  /* ──────────── Shared filter controls ──────────── */
  const renderFilterControls = ({ sidebar = false } = {}) => {
    const gridSize = sidebar ? 12 : undefined; // xs={12} for sidebar, default otherwise

    const primaryFields = (
      <>
        <Autocomplete
          options={makes}
          value={filters.make || null}
          onChange={(e, value) => handleFilterChange('make')({ target: { value: value || '' } })}
          sx={sidebar ? { width: '100%' } : { flex: '1 1 160px', minWidth: 140 }}
          renderInput={(params) => <TextField {...params} label="Make" size="small" />}
        />
        <Autocomplete
          options={availableModels}
          value={filters.model || null}
          onChange={(e, value) => handleFilterChange('model')({ target: { value: value || '' } })}
          disabled={!filters.make}
          sx={sidebar ? { width: '100%' } : { flex: '1 1 160px', minWidth: 140 }}
          renderInput={(params) => (
            <Tooltip title="Required for cross-market comparison" arrow placement="bottom">
              <TextField
                {...params}
                label="Model"
                size="small"
                placeholder={filters.make ? 'All' : ''}
              />
            </Tooltip>
          )}
        />
        <Tooltip title="Enter a ZIP code or city name" arrow placement="bottom">
          <Autocomplete
            freeSolo
            options={[...locationSuggestions, ...nominatimResults]}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.city}, ${opt.state} (${opt.zip})`}
            inputValue={locationInput}
            onInputChange={(e, val) => {
              setLocationInput(val);
              // If it's a 5-digit zip, use directly
              if (/^\d{5}$/.test(val.trim())) {
                setFilters(f => ({ ...f, zipCode: val.trim() }));
              } else {
                fetchNominatim(val);
              }
            }}
            onChange={(e, val) => {
              if (val && typeof val === 'object') {
                setFilters(f => ({ ...f, zipCode: val.zip }));
                setLocationInput(`${val.city}, ${val.state}`);
              }
            }}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase().trim();
              if (!q || /^\d/.test(q)) return [];
              // Local matches
              const local = opts.filter(o =>
                !o.source && (
                  o.city.toLowerCase().startsWith(q) ||
                  `${o.city}, ${o.state}`.toLowerCase().startsWith(q)
                )
              );
              // Nominatim matches
              const remote = opts.filter(o => o.source === 'nominatim');
              // Merge and deduplicate (by city+state)
              const seen = new Set();
              const merged = [];
              for (const o of [...local, ...remote]) {
                const key = `${o.city}|${o.state}`.toLowerCase();
                if (!seen.has(key)) { seen.add(key); merged.push(o); }
              }
              // Sort by distance to user if available
              if (userLat && userLng && merged.length > 1) {
                const dist = (o) => {
                  const lat = o.lat || 0;
                  const lng = o.lng || 0;
                  return Math.pow(lat - userLat, 2) + Math.pow(lng - userLng, 2);
                };
                merged.sort((a, b) => dist(a) - dist(b));
              }
              return merged.slice(0, 10);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City or ZIP"
                onKeyDown={handleKeyDown}
                size="small"
                sx={sidebar ? { width: '100%' } : { flex: '0 1 180px', minWidth: 140 }}
              />
            )}
            size="small"
            sx={sidebar ? { width: '100%' } : { flex: '0 1 180px', minWidth: 140 }}
          />
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => handleSearch()}
          disabled={loading}
          sx={sidebar ? { width: '100%', height: 40 } : { height: 40, px: 3, flex: '0 0 auto' }}
        >
          Search
        </Button>
      </>
    );

    const advancedFields = (
      <Grid container spacing={1.5} sx={sidebar ? {} : { mt: 0.5 }}>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <TextField fullWidth label="Year Min" type="number" size="small"
            value={filters.yearMin} onChange={handleFilterChange('yearMin')} onKeyDown={handleKeyDown} />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <TextField fullWidth label="Year Max" type="number" size="small"
            value={filters.yearMax} onChange={handleFilterChange('yearMax')} onKeyDown={handleKeyDown} />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <TextField fullWidth label="Max Price" type="number" size="small"
            value={filters.priceMax} onChange={handleFilterChange('priceMax')} onKeyDown={handleKeyDown} />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <TextField fullWidth label="Max Mileage" type="number" size="small"
            value={filters.maxMileage} onChange={handleFilterChange('maxMileage')} onKeyDown={handleKeyDown} />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <Autocomplete
            multiple
            options={exteriorColors}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
            value={exteriorColors.filter((c) => (filters.exteriorColor || []).includes(c.label))}
            isOptionEqualToValue={(opt, val) => opt.label === (val?.label || val)}
            onChange={(e, value) =>
              setFilters((prev) => ({ ...prev, exteriorColor: value.map((v) => v.label || v) }))
            }
            renderOption={(props, option) => (
              <li {...props} key={option.label}>
                <Box
                  sx={{
                    width: 16, height: 16, borderRadius: '50%', mr: 1, flexShrink: 0,
                    bgcolor: option.hex,
                    border: option.border ? `1px solid ${option.border}` : '1px solid rgba(0,0,0,0.12)',
                  }}
                />
                {option.label}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.label}
                  size="small"
                  label={option.label}
                  icon={
                    <Box
                      sx={{
                        width: 12, height: 12, borderRadius: '50%', ml: '4px !important',
                        bgcolor: option.hex,
                        border: option.border ? `1px solid ${option.border}` : '1px solid rgba(0,0,0,0.12)',
                      }}
                    />
                  }
                />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Color" size="small" />}
            limitTags={1}
          />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <FormControl fullWidth size="small">
            <InputLabel>Radius</InputLabel>
            <Select
              value={filters.radius}
              label="Radius"
              onChange={handleFilterChange('radius')}
            >
              {radiusOptions.map((opt) => (
                <MenuItem
                  key={opt}
                  value={opt}
                  disabled={(typeof opt === 'number' && opt > 100) || opt === 'nationwide'}
                >
                  {opt === 'nationwide' ? 'Nationwide' : `${opt} mi`}
                  {(typeof opt === 'number' && opt > 100) || opt === 'nationwide' ? ' (unavailable)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <Autocomplete
            multiple
            options={fuelTypes}
            value={filters.engineType || []}
            onChange={(e, value) =>
              setFilters((prev) => ({ ...prev, engineType: value }))
            }
            renderInput={(params) => <TextField {...params} label="Fuel Type" size="small" />}
            limitTags={1}
          />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <Autocomplete
            options={availableBodyTypes}
            value={filters.bodyType || null}
            onChange={(e, value) => handleFilterChange('bodyType')({ target: { value: value || '' } })}
            renderInput={(params) => <TextField {...params} label="Body Type" size="small" />}
          />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <Autocomplete
            multiple
            options={drivetrainOptions}
            value={filters.drivetrain || []}
            onChange={(e, value) =>
              setFilters((prev) => ({ ...prev, drivetrain: value }))
            }
            renderInput={(params) => <TextField {...params} label="Drivetrain" size="small" />}
            limitTags={1}
          />
        </Grid>
        <Grid item xs={gridSize || 6} sm={gridSize || 3}>
          <Tooltip
            title="When ON, excludes salvage, rebuilt, and flood-damage titles. Turn OFF to include all title types (may show lower prices but riskier vehicles)."
            arrow
            placement="top"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={filters.cleanTitle !== false}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, cleanTitle: e.target.checked }))
                  }
                  size="small"
                />
              }
              label="Clean Title Only"
              sx={{ ml: 0, mt: 0.5 }}
            />
          </Tooltip>
        </Grid>
      </Grid>
    );

    if (sidebar) {
      return (
        <>
          {primaryFields}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton
              size="small"
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ color: 'text.secondary' }}
            >
              {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showAdvanced} sx={{ width: '100%' }}>
            {advancedFields}
          </Collapse>
        </>
      );
    }

    return (
      <>
        {primaryFields}
        <IconButton
          size="small"
          onClick={() => setShowAdvanced((v) => !v)}
          sx={{ color: 'text.secondary' }}
        >
          {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Collapse in={showAdvanced} sx={{ width: '100%' }}>
          {advancedFields}
        </Collapse>
      </>
    );
  };

  /* ──────────── Reusable result sub-sections ──────────── */

  const recentSearchesBar = recentSearches.length > 0 && (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Recent:</Typography>
      {recentSearches.slice(0, 5).map((r, i) => (
        <Chip
          key={i}
          label={formatSearchLabel(r.filters)}
          size="small"
          variant="outlined"
          onClick={() => {
            const newFilters = { ...defaultFilters, ...r.filters };
            setFilters(newFilters);
            handleSearch(newFilters);
          }}
          onDelete={() => {
            const updated = recentSearches.filter((_, idx) => idx !== i);
            localStorage.setItem('recentSearches', JSON.stringify(updated));
            setRecentSearches(updated);
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      ))}
    </Box>
  );

  const marketSearchButton = (m) => (
    <Button
      size="small"
      endIcon={<OpenInNewIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        const params = new URLSearchParams();
        params.set('make', filters.make);
        if (filters.model) params.set('model', filters.model);
        params.set('yearMin', filters.yearMin);
        params.set('yearMax', filters.yearMax);
        params.set('zipCode', m.zip3 + '01');
        params.set('radius', '100');
        if (filters.priceMax) params.set('priceMax', filters.priceMax);
        if (filters.maxMileage) params.set('maxMileage', filters.maxMileage);
        if (filters.exteriorColor?.length) params.set('exteriorColor', filters.exteriorColor.join(','));
        if (filters.engineType?.length) params.set('engineType', filters.engineType.join(','));
        if (filters.bodyType) params.set('bodyType', filters.bodyType);
        if (filters.drivetrain?.length) params.set('drivetrain', filters.drivetrain.join(','));
        if (filters.cleanTitle === false) params.set('cleanTitle', 'false');
        window.open(`/?${params.toString()}`, '_blank');
      }}
      sx={{ mt: 1, p: 0, minWidth: 0, textTransform: 'none' }}
    >
      Search this market
    </Button>
  );

  const renderMarketCard = (m, i, sidebar = false) => (
    <Paper key={i} variant="outlined" sx={{ p: 1.5, ...(sidebar ? {} : { flex: '1 1 200px', maxWidth: 280 }) }}>
      <Typography variant="subtitle2" fontWeight="bold">
        {m.city && m.state ? `${m.city}, ${m.state}` : `Area ${m.zip3}xx`}
      </Typography>
      {m.city && m.state && (
        <Typography variant="caption" color="text.secondary">
          ZIP: {m.zip3}xx
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Median: ${m.median_price?.toLocaleString()} ({m.sample_size} listings)
      </Typography>
      <Tooltip title="Estimated open-carrier transport based on straight-line distance. Actual quotes vary by route, season, and vehicle size." arrow placement="top">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'help' }}>
          <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            ${m.transport_cost?.toLocaleString()} est. transport
          </Typography>
        </Box>
      </Tooltip>
      <Tooltip title="Estimated savings based on the difference between your local median price and this market's median, minus transport. Actual savings depend on the specific vehicle." arrow placement="top">
        <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ mt: 0.5, cursor: 'help' }}>
          Save ${m.net_savings?.toLocaleString()} est. net
        </Typography>
      </Tooltip>
      {marketSearchButton(m)}
    </Paper>
  );

  const betterValueSection = !loading && cheaperMarketsSearched && (
    cheaperMarkets.length > 0 ? (
      <Paper sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TrendingDownIcon color="success" />
          <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
            Better Value Markets Found
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Similar vehicles may be available for less in other areas. Savings shown are after estimated transport cost.
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
          Estimates only. Savings reflect median listing price differences and do not account for trim, mileage, condition, taxes, or dealer fees. Transport costs are based on straight-line distance and average open-carrier rates. Sales tax varies by state (0% in OR, MT, NH to 10%+ elsewhere). Always verify with actual listings and get carrier quotes before committing.
        </Typography>
        <Box sx={isDesktop
          ? { display: 'flex', flexDirection: 'column', gap: 1.5 }
          : { display: 'flex', flexWrap: 'wrap', gap: 1.5 }
        }>
          {cheaperMarkets.slice(0, 5).map((m, i) => renderMarketCard(m, i, isDesktop))}
        </Box>
      </Paper>
    ) : (
      <Alert severity="info" sx={{ mt: 2 }}>
        No better value markets found — this vehicle is competitively priced in your area compared to other US markets.
      </Alert>
    )
  );

  const resultsHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      <Typography variant="h5" fontWeight="bold">
        {results.length} result{results.length !== 1 ? 's' : ''}
        {filters.make && ` for ${filters.make}`}
        {filters.model && ` ${filters.model}`}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="price_asc">Price: Low to High</MenuItem>
            <MenuItem value="price_desc">Price: High to Low</MenuItem>
            <MenuItem value="miles_asc">Mileage: Low to High</MenuItem>
            <MenuItem value="miles_desc">Mileage: High to Low</MenuItem>
            <MenuItem value="year_desc">Year: Newest</MenuItem>
            <MenuItem value="year_asc">Year: Oldest</MenuItem>
            <MenuItem value="savings">Best Deals</MenuItem>
            <MenuItem value="newest">Newest Listings</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" onClick={handleClearResults}>Clear results</Button>
        {!isAnonymous && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleSaveSearch}
            disabled={savingSearch}
          >
            {savingSearch ? 'Saving...' : 'Save Search'}
          </Button>
        )}
      </Box>
    </Box>
  );

  /* ──────────── Search Results Page ──────────── */
  if (hasResults || loading) {
    // Desktop: split-column layout
    if (isDesktop) {
      return (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Left sidebar — sticky filters + market analysis */}
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
            }}
          >
            <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {renderFilterControls({ sidebar: true })}
            </Paper>
            {betterValueSection}
          </Box>

          {/* Right column — results */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {recentSearchesBar}

            {error && (
              <Alert severity={hasResults ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {resultsHeader}
                <Grid container spacing={2}>
                  {sortedResults.map((v) => (
                    <Grid item xs={12} sm={6} lg={4} key={v.id}>
                      <VehicleCard vehicle={v} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            <Footer />
          </Box>
        </Box>
      );
    }

    // Mobile/tablet: stacked layout
    return (
      <Box>
        {/* Compact search filters */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {renderFilterControls()}
        </Paper>

        {recentSearchesBar}

        {/* Market summary banner — links to full details below results */}
        {!loading && cheaperMarketsSearched && cheaperMarkets.length > 0 && (
          <Paper
            sx={{
              p: 1.5, mb: 2, borderRadius: 2,
              bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingDownIcon color="success" fontSize="small" />
              <Typography variant="body2" fontWeight="bold" color="success.dark">
                {cheaperMarkets.length} cheaper market{cheaperMarkets.length !== 1 ? 's' : ''} found
              </Typography>
            </Box>
            <Button
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => document.getElementById('market-details')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View details
            </Button>
          </Paper>
        )}

        {error && (
          <Alert severity={hasResults ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {resultsHeader}
            <Grid container spacing={2}>
              {sortedResults.map((v) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={v.id}>
                  <VehicleCard vehicle={v} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Full market analysis details — below results */}
        <Box id="market-details">
          {betterValueSection}
        </Box>
        <Footer />
      </Box>
    );
  }

  /* ──────────── Landing Page ──────────── */
  return (
    <Box sx={{ mx: -3, mt: -3, mb: -3, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #0a1929 0%, #1a3a5c 40%, #0d2137 100%)',
          color: 'white',
          py: 10,
          px: 3,
          zIndex: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'radial-gradient(ellipse at 80% 50%, rgba(25, 118, 210, 0.15) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ mb: 1, fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3rem' } }}
          >
            Find Your Next Vehicle. From the{' '}
            <Box component="span" sx={{ color: '#4fc3f7' }}>Right Market</Box>.
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, opacity: 0.8, fontWeight: 'normal' }}>
            Compare prices across markets. Discover deals others miss.
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, opacity: 0.5 }}>
            Built for smart buyers and independent dealers.
          </Typography>

          <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {renderFilterControls()}
          </Paper>

          {recentSearches.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <HistoryIcon fontSize="small" sx={{ opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ opacity: 0.6, mr: 0.5, flexShrink: 0 }}>Recent:</Typography>
              {recentSearches.slice(0, 3).map((r, i) => (
                <Chip
                  key={i}
                  label={formatSearchLabel(r.filters)}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                  onClick={() => setFilters({ ...defaultFilters, ...r.filters })}
                  onDelete={() => {
                    const updated = recentSearches.filter((_, idx) => idx !== i);
                    localStorage.setItem('recentSearches', JSON.stringify(updated));
                    setRecentSearches(updated);
                  }}
                  deleteIcon={<CloseIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5) !important' }} />}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Featured vehicles */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 3, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {featuredLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <ScrollRow
            vehicles={featured}
            title={
              userLocation
                ? `Recently Listed near ${userLocation.city}, ${userLocation.region}`
                : 'Recently Listed Vehicles'
            }
          />
        )}
        <Footer sx={{ mt: 4, borderColor: 'rgba(255,255,255,0.12)', color: 'text.secondary' }} />
      </Box>
    </Box>
  );
}

export default VehicleSearch;
